// Detects the visitor's country (IP-based, via GET /geo/country) and returns the
// matching country_pricing config for the Lifetime Access plan — currency, price,
// tax rate/label, and which payment gateways are available for that country. Result
// is cached in localStorage for 24h so repeat visits don't re-hit the geo API, and
// `setCountry` lets the user manually override the detected country (e.g. a country
// switcher on the pricing page).
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '@/lib/apiBase';

const API_URL = getApiBaseUrl();
const STORAGE_KEY = 'thachari_country';
const CACHE_HOURS = 24;

export function useCountryPricing() {
  const [countryData, setCountryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const detectCountry = useCallback(async () => {
    setLoading(true);
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const hoursSince = (Date.now() - timestamp) / (1000 * 60 * 60);
        if (hoursSince < CACHE_HOURS && data?.pricing) {
          setCountryData(data);
          setLoading(false);
          return;
        }
      }
    } catch {
      // Corrupt/old cache shape — ignore and re-fetch
    }

    try {
      const { data } = await axios.get(`${API_URL}/geo/country`);
      setCountryData(data);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
      } catch {
        // localStorage unavailable (private browsing, quota) — non-fatal
      }
    } catch (err) {
      setError(err);
      // Fallback to India pricing so the pricing page still renders a price
      try {
        const { data } = await axios.get(`${API_URL}/country-pricing/IN`);
        setCountryData({ country_code: 'IN', pricing: data });
      } catch {
        // Backend fully unreachable — caller's `pricing` stays null, page shows its own fallback
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    detectCountry();
  }, [detectCountry]);

  const setCountry = async (countryCode) => {
    try {
      const { data } = await axios.get(`${API_URL}/country-pricing/${countryCode}`);
      const newData = { country_code: countryCode, pricing: data };
      setCountryData(newData);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ data: newData, timestamp: Date.now() }));
      } catch {
        // non-fatal
      }
    } catch (err) {
      console.error('Failed to switch country:', err);
    }
  };

  return {
    countryData,
    pricing: countryData?.pricing || null,
    countryCode: countryData?.country_code || 'IN',
    loading,
    error,
    setCountry,
  };
}

export default useCountryPricing;
